import { Fragment, type ReactNode } from 'react';

/**
 * A narrative renderer for exactly the markdown `work.pulse` produces.
 *
 * **Hand-rolled rather than a dependency, and the subset is measured.**
 * Across the 40 most recent narratives on the live store:
 *
 * | construct | occurrences |
 * |---|---:|
 * | ticket keys (`[A-Z]{2,}-\d+`) | 871 |
 * | paragraphs | 275 |
 * | `###` headings | 235 |
 * | `-` bullets | 235 |
 * | `##` headings | 80 |
 * | `**bold**` | 40 |
 * | italics, code, links, tables, ordered lists, blockquotes, fences | **0** |
 *
 * Six constructs, none of them nested beyond bold-inside-text. The
 * predecessor used `react-markdown` and then overrode every element anyway
 * — h2, h3, p, strong, ul, li and a — to inject the slug anchors the
 * cross-linking needs, so it was already most of a renderer with a parser
 * bolted underneath. `CLAUDE.md` asks that a dependency not be added to
 * solve what is an interface problem.
 *
 * **Headings carry a slug id.** That is the whole mechanism behind clicking
 * a finding and having the narrative scroll to the matching section: the
 * chip and the heading agree on `slugify(headline)`.
 *
 * **Ticket keys are styled, not linked.** Linking needs the Jira site URL,
 * which lives in `keys.json` beside credentials and has no read endpoint
 * here. Recorded in `TODO.md`; a key you can select beats a key that looks
 * clickable and is not.
 */

/** Project key of 2+ chars, so `A-1` in prose is not mistaken for a ticket. */
const TICKET = /\b[A-Z][A-Z0-9]+-\d+\b/g;
const BOLD = /\*\*([^*]+)\*\*/g;

/** Lowercase, non-alphanumerics to hyphens, trimmed. Must match the chip's. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Ticket keys as marked spans. Applied inside bold and plain runs alike. */
function withTickets(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TICKET)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <span className="nb-ticket" key={`${keyPrefix}-t${at}`}>
        {m[0]}
      </span>,
    );
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** `**bold**` plus ticket keys. The only inline markup that occurs. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(BOLD)) {
    const at = m.index ?? 0;
    if (at > last) {
      out.push(...withTickets(text.slice(last, at), `${keyPrefix}-p${at}`));
    }
    out.push(
      <strong key={`${keyPrefix}-b${at}`}>
        {withTickets(m[1] ?? '', `${keyPrefix}-b${at}`)}
      </strong>,
    );
    last = at + m[0].length;
  }
  if (last < text.length) {
    out.push(...withTickets(text.slice(last), `${keyPrefix}-p${last}`));
  }
  return out;
}

interface Props {
  text: string;
  /** Slug to highlight, from a finding chip the reader just clicked. */
  highlightedSlug?: string | null;
}

export function NarrativeMarkdown({ text, highlightedSlug }: Props) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = (at: number) => {
    if (bullets.length === 0) return;
    blocks.push(
      <ul className="nb-list" key={`ul-${at}`}>
        {bullets.map((b, i) => (
          <li key={i}>{inline(b, `ul-${at}-${i}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  text.split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (!line) {
      flushBullets(i);
      return;
    }
    const heading = /^(#{2,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushBullets(i);
      const depth = heading[1]?.length ?? 2;
      const body = heading[2] ?? '';
      const slug = slugify(body);
      const highlighted = highlightedSlug === slug;
      // The id is the cross-link target. `finding-` prefixed so a chip can
      // scroll to it without colliding with any other element id.
      const props = {
        id: `finding-${slug}`,
        className: `nb-h${depth}${highlighted ? ' is-highlighted' : ''}`,
      };
      blocks.push(
        depth === 2 ? (
          <h2 {...props} key={i}>
            {inline(body, `h-${i}`)}
          </h2>
        ) : (
          <h3 {...props} key={i}>
            {inline(body, `h-${i}`)}
          </h3>
        ),
      );
      return;
    }
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      bullets.push(bullet[1] ?? '');
      return;
    }
    flushBullets(i);
    blocks.push(
      <p className="nb-p" key={i}>
        {inline(line, `p-${i}`)}
      </p>,
    );
  });
  flushBullets(text.length);

  return <Fragment>{blocks}</Fragment>;
}
